<template>
  <div class="generation-preferences">
    <!-- Collapsible Header -->
    <button
      @click="isExpanded = !isExpanded"
      class="w-full flex items-center justify-between p-3 bg-white border border-gray-200 rounded-lg shadow-sm hover:bg-gray-50 transition-colors"
      :class="{ 'rounded-b-none border-b-0': isExpanded }"
    >
      <div class="flex items-center space-x-2">
        <CogIcon class="w-5 h-5 text-blue-600" />
        <span class="text-sm font-medium text-gray-900">Generation Settings</span>
        <span
          class="px-2 py-1 text-xs rounded-full font-medium"
          :class="getPreferredSourceBadgeClass()"
        >
          {{ getPreferredSourceText() }}
        </span>
      </div>
      
      <div class="flex items-center space-x-2">
        <span class="text-xs text-gray-500">{{ getSuccessRate() }}% success</span>
        <ChevronDownIcon
          class="w-4 h-4 text-gray-500 transition-transform duration-200"
          :class="{ 'rotate-180': isExpanded }"
        />
      </div>
    </button>

    <!-- Expandable Content -->
    <div
      v-if="isExpanded"
      class="border border-t-0 border-gray-200 rounded-b-lg bg-white shadow-sm"
    >
      <div class="p-4 space-y-6">
        
        <!-- Generation Method -->
        <div class="space-y-3">
          <div class="flex items-center justify-between">
            <label class="text-sm font-medium text-gray-700">Generation Method</label>
            <button
              @click="showHelp = !showHelp"
              class="text-xs text-blue-600 hover:text-blue-800 flex items-center space-x-1"
            >
              <QuestionMarkCircleIcon class="w-3 h-3" />
              <span>Help</span>
            </button>
          </div>

          <div class="grid grid-cols-3 gap-2">
            <button
              v-for="option in sourceOptions"
              :key="option.value"
              @click="updatePreference('sourcePreference', option.value)"
              class="relative p-3 rounded-lg border transition-all duration-200 text-center"
              :class="getSourceOptionClass(option.value)"
              :disabled="!isSourceAvailable(option.value)"
            >
              <component :is="option.icon" class="w-5 h-5 mx-auto mb-2" />
              <div class="text-xs font-medium">{{ option.label }}</div>
              <div class="text-xs text-gray-500 mt-1">{{ option.description }}</div>
              
              <div
                v-if="!isSourceAvailable(option.value)"
                class="absolute inset-0 bg-gray-100/80 rounded-lg flex items-center justify-center"
              >
                <span class="text-xs text-gray-500 font-medium">Offline</span>
              </div>
            </button>
          </div>

          <div
            v-if="showHelp"
            class="p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800"
          >
            <div class="space-y-1">
              <div><strong>Auto:</strong> Intelligently selects the best method</div>
              <div><strong>Frontend:</strong> Fast client-side generation</div>
              <div><strong>Backend:</strong> Full-featured server processing</div>
            </div>
          </div>
        </div>

        <!-- Quick Settings -->
        <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <!-- Real-time Preview -->
          <div class="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
            <div>
              <div class="text-sm font-medium text-gray-700">Real-time Preview</div>
              <div class="text-xs text-gray-500">Generate as you type</div>
            </div>
            <label class="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                :checked="preferences.instantPreview"
                @change="updatePreference('instantPreview', $event.target.checked)"
                :disabled="!serviceStatus?.frontend?.available"
                class="sr-only"
              />
              <div
                class="w-11 h-6 rounded-full transition-colors duration-200"
                :class="preferences.instantPreview && serviceStatus?.frontend?.available ? 'bg-blue-600' : 'bg-gray-200'"
              ></div>
              <div
                class="absolute left-[2px] top-[2px] bg-white w-5 h-5 rounded-full transition-transform duration-200 shadow-sm"
                :class="preferences.instantPreview && serviceStatus?.frontend?.available ? 'translate-x-5' : 'translate-x-0'"
              ></div>
            </label>
          </div>

          <!-- Auto Retry -->
          <div class="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
            <div>
              <div class="text-sm font-medium text-gray-700">Auto Retry</div>
              <div class="text-xs text-gray-500">Retry failed generations</div>
            </div>
            <label class="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                :checked="preferences.autoRetry"
                @change="updatePreference('autoRetry', $event.target.checked)"
                class="sr-only"
              />
              <div
                class="w-11 h-6 rounded-full transition-colors duration-200"
                :class="preferences.autoRetry ? 'bg-blue-600' : 'bg-gray-200'"
              ></div>
              <div
                class="absolute left-[2px] top-[2px] bg-white w-5 h-5 rounded-full transition-transform duration-200 shadow-sm"
                :class="preferences.autoRetry ? 'translate-x-5' : 'translate-x-0'"
              ></div>
            </label>
          </div>
        </div>

        <!-- Preview Delay Slider -->
        <div
          v-if="preferences.instantPreview && serviceStatus?.frontend?.available"
          class="space-y-2"
        >
          <div class="flex items-center justify-between">
            <label class="text-sm font-medium text-gray-700">Preview Delay</label>
            <span class="text-sm text-gray-500">{{ preferences.realtimeDelay }}ms</span>
          </div>
          <input
            type="range"
            :value="preferences.realtimeDelay"
            min="100"
            max="2000"
            step="100"
            @input="updatePreference('realtimeDelay', parseInt($event.target.value))"
            class="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider"
          />
          <div class="flex justify-between text-xs text-gray-400">
            <span>Instant</span>
            <span>Delayed</span>
          </div>
        </div>

        <!-- Advanced Settings -->
        <details class="group">
          <summary class="flex items-center justify-between cursor-pointer text-sm font-medium text-gray-700 hover:text-gray-900">
            <span>Advanced Settings</span>
            <ChevronDownIcon class="w-4 h-4 transition-transform group-open:rotate-180" />
          </summary>
          
          <div class="mt-4 space-y-4 pl-4 border-l-2 border-gray-100">
            <!-- Performance Mode -->
            <div class="flex items-center justify-between">
              <div>
                <div class="text-sm font-medium text-gray-700">Performance Mode</div>
                <div class="text-xs text-gray-500">Optimize for speed over quality</div>
              </div>
              <label class="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  :checked="preferences.performanceMode"
                  @change="updatePreference('performanceMode', $event.target.checked)"
                  class="sr-only"
                />
                <div
                  class="w-11 h-6 rounded-full transition-colors duration-200"
                  :class="preferences.performanceMode ? 'bg-blue-600' : 'bg-gray-200'"
                ></div>
                <div
                  class="absolute left-[2px] top-[2px] bg-white w-5 h-5 rounded-full transition-transform duration-200 shadow-sm"
                  :class="preferences.performanceMode ? 'translate-x-5' : 'translate-x-0'"
                ></div>
              </label>
            </div>

            <!-- Clear on Tab Change -->
            <div class="flex items-center justify-between">
              <div>
                <div class="text-sm font-medium text-gray-700">Clear on Type Change</div>
                <div class="text-xs text-gray-500">Clear QR when switching content types</div>
              </div>
              <label class="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  :checked="preferences.clearOnTabChange"
                  @change="updatePreference('clearOnTabChange', $event.target.checked)"
                  class="sr-only"
                />
                <div
                  class="w-11 h-6 rounded-full transition-colors duration-200"
                  :class="preferences.clearOnTabChange ? 'bg-blue-600' : 'bg-gray-200'"
                ></div>
                <div
                  class="absolute left-[2px] top-[2px] bg-white w-5 h-5 rounded-full transition-transform duration-200 shadow-sm"
                  :class="preferences.clearOnTabChange ? 'translate-x-5' : 'translate-x-0'"
                ></div>
              </label>
            </div>

            <!-- Batch Threshold -->
            <div class="space-y-2">
              <div class="flex items-center justify-between">
                <label class="text-sm font-medium text-gray-700">Batch Threshold</label>
                <span class="text-sm text-gray-500">{{ preferences.batchThreshold }} QRs</span>
              </div>
              <input
                type="range"
                :value="preferences.batchThreshold"
                min="5"
                max="100"
                step="5"
                @input="updatePreference('batchThreshold', parseInt($event.target.value))"
                class="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider"
              />
              <div class="text-xs text-gray-500">
                Use backend processing for bulk operations above this threshold
              </div>
            </div>

            <!-- Max Retry Attempts -->
            <div v-if="preferences.autoRetry" class="space-y-2">
              <div class="flex items-center justify-between">
                <label class="text-sm font-medium text-gray-700">Max Retry Attempts</label>
                <span class="text-sm text-gray-500">{{ preferences.maxRetryAttempts }}</span>
              </div>
              <input
                type="range"
                :value="preferences.maxRetryAttempts"
                min="1"
                max="5"
                @input="updatePreference('maxRetryAttempts', parseInt($event.target.value))"
                class="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider"
              />
            </div>
          </div>
        </details>

        <!-- Session Stats -->
        <div class="bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg p-4">
          <div class="text-sm font-medium text-gray-700 mb-3">Session Statistics</div>
          <div class="grid grid-cols-2 sm:grid-cols-4 gap-4 text-center">
            <div>
              <div class="text-lg font-bold text-blue-600">{{ stats.totalAttempts || 0 }}</div>
              <div class="text-xs text-gray-600">Total</div>
            </div>
            <div>
              <div class="text-lg font-bold" :class="getSuccessRateClass()">
                {{ getSuccessRate() }}%
              </div>
              <div class="text-xs text-gray-600">Success</div>
            </div>
            <div>
              <div class="text-lg font-bold text-emerald-600">{{ stats.frontendCount || 0 }}</div>
              <div class="text-xs text-gray-600">Frontend</div>
            </div>
            <div>
              <div class="text-lg font-bold text-indigo-600">{{ stats.backendCount || 0 }}</div>
              <div class="text-xs text-gray-600">Backend</div>
            </div>
          </div>
        </div>

        <!-- Action Buttons -->
        <div class="flex items-center justify-between pt-4 border-t border-gray-100">
          <div class="flex items-center space-x-3">
            <button
              @click="resetToDefaults"
              class="text-sm text-gray-600 hover:text-gray-800 underline"
            >
              Reset Defaults
            </button>
            <button
              @click="exportSettings"
              class="text-sm text-blue-600 hover:text-blue-800 underline"
            >
              Export Settings
            </button>
          </div>

          <div v-if="saveStatus" class="flex items-center space-x-2 text-sm">
            <div
              v-if="saveStatus === 'saving'"
              class="flex items-center space-x-2 text-blue-600"
            >
              <div class="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
              <span>Saving...</span>
            </div>
            <div
              v-else-if="saveStatus === 'saved'"
              class="flex items-center space-x-2 text-emerald-600"
            >
              <CheckIcon class="w-4 h-4" />
              <span>Saved</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Export Modal -->
    <teleport to="body">
      <div
        v-if="showExportModal"
        class="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
        @click.self="showExportModal = false"
      >
        <div class="bg-white rounded-lg shadow-xl max-w-md w-full">
          <div class="p-4 border-b border-gray-200">
            <h3 class="text-lg font-semibold text-gray-900">Export/Import Settings</h3>
          </div>
          <div class="p-4 space-y-4">
            <textarea
              v-model="exportData"
              rows="8"
              class="w-full text-sm border border-gray-300 rounded-md px-3 py-2 font-mono focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
              placeholder="Paste settings JSON here to import..."
            ></textarea>
            <div class="flex justify-between">
              <button
                @click="importSettings"
                :disabled="!exportData.trim()"
                class="px-4 py-2 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700 disabled:bg-gray-300"
              >
                Import
              </button>
              <div class="flex space-x-2">
                <button
                  @click="copyToClipboard"
                  class="px-4 py-2 bg-gray-600 text-white text-sm rounded-md hover:bg-gray-700"
                >
                  Copy
                </button>
                <button
                  @click="showExportModal = false"
                  class="px-4 py-2 bg-gray-300 text-gray-700 text-sm rounded-md hover:bg-gray-400"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </teleport>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue'
import {
  CogIcon,
  QuestionMarkCircleIcon,
  ChevronDownIcon,
  CheckIcon,
  BoltIcon,
  ServerIcon,
  SparklesIcon,
} from '@heroicons/vue/24/outline'

// Props
const props = defineProps({
  modelValue: {
    type: Object,
    default: () => ({})
  },
  serviceStatus: {
    type: Object,
    default: () => ({})
  },
  generationAttempts: {
    type: Number,
    default: 0
  }
})

// Emits
const emit = defineEmits(['update:modelValue', 'notification'])

// State
const isExpanded = ref(false)
const showHelp = ref(false)
const showExportModal = ref(false)
const exportData = ref('')
const saveStatus = ref(null)

// Default preferences
const defaultPreferences = {
  sourcePreference: 'auto',
  instantPreview: true,
  realtimeDelay: 500,
  autoRetry: true,
  maxRetryAttempts: 3,
  clearOnTabChange: false,
  performanceMode: false,
  batchThreshold: 20,
  healthCheckInterval: 30000,
  showServiceNotifications: true,
}

// Reactive preferences
const preferences = ref({
  ...defaultPreferences,
  ...props.modelValue,
})

// Stats
const stats = ref({
  frontendCount: 0,
  backendCount: 0,
  totalAttempts: 0,
  successfulAttempts: 0,
})

// Source options
const sourceOptions = [
  {
    value: 'auto',
    label: 'Auto',
    description: 'Smart selection',
    icon: SparklesIcon,
  },
  {
    value: 'frontend',
    label: 'Frontend',
    description: 'Fast & instant',
    icon: BoltIcon,
  },
  {
    value: 'backend',
    label: 'Backend',
    description: 'Full features',
    icon: ServerIcon,
  },
]

// Computed
const getPreferredSourceText = () => {
  const preference = preferences.value.sourcePreference
  return preference.charAt(0).toUpperCase() + preference.slice(1)
}

const getPreferredSourceBadgeClass = () => {
  const source = preferences.value.sourcePreference
  
  switch (source) {
    case 'frontend':
      return 'bg-emerald-100 text-emerald-800'
    case 'backend':
      return 'bg-blue-100 text-blue-800'
    default:
      return 'bg-purple-100 text-purple-800'
  }
}

const getSuccessRate = () => {
  if (stats.value.totalAttempts === 0) return 100
  return Math.round((stats.value.successfulAttempts / stats.value.totalAttempts) * 100)
}

const getSuccessRateClass = () => {
  const rate = getSuccessRate()
  if (rate >= 90) return 'text-emerald-600'
  if (rate >= 70) return 'text-amber-600'
  return 'text-red-600'
}

// Methods
const isSourceAvailable = (source) => {
  if (source === 'auto') return true
  if (source === 'frontend') return props.serviceStatus?.frontend?.available !== false
  if (source === 'backend') return props.serviceStatus?.backend?.available !== false
  return false
}

const getSourceOptionClass = (source) => {
  const isSelected = preferences.value.sourcePreference === source
  const isAvailable = isSourceAvailable(source)
  
  let classes = 'relative p-3 rounded-lg border transition-all duration-200 text-center '
  
  if (!isAvailable) {
    classes += 'border-gray-200 bg-gray-50 text-gray-400 cursor-not-allowed'
  } else if (isSelected) {
    switch (source) {
      case 'frontend':
        classes += 'border-emerald-500 bg-emerald-50 text-emerald-700 ring-2 ring-emerald-200'
        break
      case 'backend':
        classes += 'border-blue-500 bg-blue-50 text-blue-700 ring-2 ring-blue-200'
        break
      default:
        classes += 'border-purple-500 bg-purple-50 text-purple-700 ring-2 ring-purple-200'
    }
  } else {
    classes += 'border-gray-300 bg-white text-gray-700 hover:border-gray-400 hover:bg-gray-50'
  }
  
  return classes
}

const updatePreference = async (key, value) => {
  preferences.value[key] = value
  
  saveStatus.value = 'saving'
  
  // Emit update
  emit('update:modelValue', { ...preferences.value })
  
  // Simulate save
  setTimeout(() => {
    saveStatus.value = 'saved'
    setTimeout(() => {
      saveStatus.value = null
    }, 2000)
  }, 300)
}

const resetToDefaults = () => {
  Object.keys(defaultPreferences).forEach(key => {
    preferences.value[key] = defaultPreferences[key]
  })
  emit('update:modelValue', { ...preferences.value })
  emit('notification', { type: 'success', message: 'Settings reset to defaults' })
}

const exportSettings = () => {
  exportData.value = JSON.stringify(preferences.value, null, 2)
  showExportModal.value = true
}

const importSettings = () => {
  try {
    const imported = JSON.parse(exportData.value)
    Object.keys(imported).forEach(key => {
      if (key in defaultPreferences) {
        preferences.value[key] = imported[key]
      }
    })
    emit('update:modelValue', { ...preferences.value })
    showExportModal.value = false
    emit('notification', { type: 'success', message: 'Settings imported successfully' })
  } catch (error) {
    emit('notification', { type: 'error', message: 'Invalid settings format' })
  }
}

const copyToClipboard = async () => {
  try {
    await navigator.clipboard.writeText(exportData.value)
    emit('notification', { type: 'success', message: 'Settings copied to clipboard' })
  } catch (error) {
    emit('notification', { type: 'error', message: 'Failed to copy settings' })
  }
}

// Watch for changes
watch(() => props.modelValue, (newValue) => {
  preferences.value = { ...defaultPreferences, ...newValue }
}, { deep: true })

watch(() => props.generationAttempts, (newAttempts, oldAttempts) => {
  if (newAttempts > oldAttempts) {
    stats.value.totalAttempts++
  }
})

// Load stats on mount
onMounted(() => {
  try {
    const savedStats = localStorage.getItem('qr-generation-stats')
    if (savedStats) {
      stats.value = { ...stats.value, ...JSON.parse(savedStats) }
    }
  } catch (error) {
    console.warn('Failed to load stats from localStorage')
  }
})
</script>

<style scoped>
/* Custom slider styles */
.slider::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #3b82f6;
  cursor: pointer;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.slider::-moz-range-thumb {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #3b82f6;
  cursor: pointer;
  border: none;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

/* Smooth transitions */
.transition-all {
  transition-property: all;
  transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
  transition-duration: 200ms;
}

.transition-colors {
  transition-property: color, background-color, border-color;
  transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
  transition-duration: 150ms;
}

.transition-transform {
  transition-property: transform;
  transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
  transition-duration: 150ms;
}

/* Animation */
@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.animate-spin {
  animation: spin 1s linear infinite;
}

/* Responsive adjustments */
@media (max-width: 640px) {
  .grid-cols-3 {
    grid-template-columns: 1fr;
  }
  
  .sm\:grid-cols-2 {
    grid-template-columns: 1fr;
  }
  
  .sm\:grid-cols-4 {
    grid-template-columns: repeat(2, 1fr);
  }
}
</style>